import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Stack,
  Fade,
  Grow,
} from "@mui/material";
import {
  ChevronRight,
} from "@mui/icons-material";
import { styled, keyframes } from "@mui/material/styles";

// You can replace this with your actual image
import intelligenceImg from "../../../images/collaborate.jpg";

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

// Styled Components
const BackgroundBox = styled(Box)(({ theme }) => ({
  position: "relative",
  minHeight: "100vh",
  background: "linear-gradient(135deg, #000000 0%, #0a0a0a 50%, #000000 100%)",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    right: 0,
    width: "50%",
    height: "50%",
    background: "radial-gradient(circle, rgba(193, 227, 40, 0.15) 0%, transparent 70%)",
    pointerEvents: "none",
  },
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: 0,
    width: "50%",
    height: "50%",
    background: "radial-gradient(circle, rgba(193, 227, 40, 0.15) 0%, transparent 70%)",
    pointerEvents: "none",
  },
}));

const FloatingParticle = styled(Box)(({ delay, duration, left, top }) => ({
  position: "absolute",
  width: 4,
  height: 4,
  borderRadius: "50%",
  background: "rgba(193, 227, 40, 0.3)",
  left: `${left}%`,
  top: `${top}%`,
  animation: `${pulse} ${duration}s ease-in-out infinite`,
  animationDelay: `${delay}s`,
}));

const GlowCard = styled(Card)(({ theme }) => ({
  position: "relative",
  background: "rgba(0, 0, 0, 0.9)",
  backdropFilter: "blur(20px)",
  borderRadius: 24,
  [theme.breakpoints.down('sm')]: {
    borderRadius: 16,
  },
  border: "1px solid rgba(193, 227, 40, 0.3)",
  overflow: "hidden",
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
}));

const ProductImage = styled(Box)(({ theme }) => ({
  position: "relative",
  height: 600,
  [theme.breakpoints.down('md')]: {
    height: 400,
  },
  [theme.breakpoints.down('sm')]: {
    height: 300,
  },
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    background: "linear-gradient(135deg, rgba(193, 227, 40, 0.15) 0%, rgba(193, 227, 40, 0.1) 50%, transparent 100%)",
    zIndex: 1,
    opacity: 1,
  },
  "& img": {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
}));

const GradientButton = styled(Button)(({ theme }) => ({
  background: "linear-gradient(135deg, #c1e328 0%, #a0c020 100%)",
  borderRadius: 16,
  padding: "16px 40px",
  [theme.breakpoints.down('sm')]: {
    padding: "12px 28px",
    fontSize: "1rem",
    borderRadius: 12,
  },
  fontSize: "1.1rem",
  fontWeight: 600,
  textTransform: "none",
  color: "#000",
  position: "relative",
  overflow: "hidden",
  border: "1px solid rgba(193, 227, 40, 0.2)",
  boxShadow: "0 4px 16px rgba(193, 227, 40, 0.2)",
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 6px 20px rgba(193, 227, 40, 0.3)",
  },
}));

const IntelligencePartner = () => {
  const navigate = useNavigate();

  const descriptions = [
    "At Autogenbot, We Collaborate to bring potential beyond limits with AI and Computer vision to expand capabilities, accelerate commercialization and stay ahead in market.",
    "Let's explore how We Can power your next breakthrough.",
  ];

  const handlePartnerClick = () => {
    navigate("/contact");
  };

  return (
    <BackgroundBox
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        py: { xs: 4, sm: 6, md: 10 },
        px: { xs: 2, sm: 4, md: 8 },
      }}
    >
      {/* Floating Particles */}
      {[...Array(20)].map((_, i) => (
        <FloatingParticle
          key={i}
          delay={Math.random() * 3}
          duration={2 + Math.random() * 3}
          left={Math.random() * 100}
          top={Math.random() * 100}
        />
      ))}

      <Container maxWidth="xl">
        {/* Section Header */}
        <Fade in timeout={1000}>
          <Box textAlign="center" mb={{ xs: 4, sm: 6, md: 8 }}>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: "1.5rem", sm: "2rem", md: "2.5rem" },
                fontWeight: 700,
                color: "#fff",
                letterSpacing: -1,
                mb: 2,
              }}
            >
              Let's Build The{" "}
              <Box
                component="span"
                sx={{
                  background: "linear-gradient(135deg, #c1e328 0%, #a0c020 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Future Together
              </Box>
            </Typography>
          </Box>
        </Fade>

        {/* Main Card */}
        <Grow in timeout={1200}>
          <GlowCard>
            <Grid container>
              {/* Content Section - LEFT */}
              <Grid item xs={12} lg={6}>
                <CardContent sx={{ p: { xs: 3, sm: 4, md: 8 }, height: "100%", display: "flex", flexDirection: "column", justifyContent: "center" }}>
                  <Stack spacing={{ xs: 3, md: 4 }}>
                    {/* Title */}
                    <Box>
                      <Typography
                        variant="h2"
                        sx={{
                          fontSize: { xs: "1.75rem", sm: "2.15rem", lg: "3.15rem" },
                          fontWeight: 700,
                          color: "#fff",
                          letterSpacing: -1,
                          mb: { xs: 2, md: 3 },
                        }}
                      >
                        Intelligence{" "}
                        <Box
                          component="span"
                          sx={{
                            background: "linear-gradient(135deg, #c1e328 0%, #a0c020 100%)",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                          }}
                        >
                          Partner With Us
                        </Box>
                      </Typography>
                    </Box>

                    {/* Descriptions */}
                    <Stack spacing={{ xs: 2, md: 3 }}>
                      {descriptions.map((desc, idx) => (
                        <Fade in timeout={1500 + idx * 200} key={idx}>
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              color: "rgba(203, 213, 225, 1)", 
                              lineHeight: 1.8,
                              fontSize: { xs: "1rem", md: "1.125rem" }
                            }}
                          >
                            {desc}
                          </Typography>
                        </Fade>
                      ))}
                    </Stack>

                    {/* CTA Button */}
                    <Box>
                      <GradientButton
                        endIcon={<ChevronRight />}
                        size="large"
                        onClick={handlePartnerClick}
                      >
                        Partner with Us
                      </GradientButton>
                    </Box>

                  </Stack>
                </CardContent>
              </Grid>

              {/* Image Section - RIGHT */}
              <Grid item xs={12} lg={6}>
                <ProductImage>
                  <img src={intelligenceImg} alt="Intelligence Partner" />
                </ProductImage>
              </Grid>
            </Grid>
          </GlowCard>
        </Grow>

      </Container>
    </BackgroundBox>
  );
};

export default IntelligencePartner;
